import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  CASAMAPA_PARTNER_FREE_TOOLS,
  CASAMAPA_PARTNER_MARKET_INTEL_TOOL_KEYS,
  CASAMAPA_PARTNER_WORKSPACE_TOOL_KEYS,
} from "../../constants/casamapaPartnerFreeTools";
import PartnerCasaMapaMarketSnapshot from "../../components/casamap/partner/PartnerCasaMapaMarketSnapshot";
import { fetchCasaMapaPartnerMarketTrends } from "../../services/casamapaPartnerAgencyApi";
import { useCurrency, useLocale } from "../../intl";
import "../../styles/partnerCasaMapaTools.css";
import "../../styles/partnerCasaMapaMarketTools.css";

const TOOLS_COPY = "realEstate.casaMapa.admin.partnerTools";
const MARKET_COPY = "realEstate.casaMapa.admin.partnerMarket";
const AD_ITEMS = "realEstate.casaMapa.advertise.freeTools.items";

function FreeToolCard({ toolKey, path, t }) {
  const meta = CASAMAPA_PARTNER_FREE_TOOLS[toolKey];
  if (!meta) return null;

  const destination = path(meta.portalSegment);
  const available = meta.available !== false;
  const live = available && meta.status === "live";
  const ctaLabel = meta.ctaKey
    ? t(`${TOOLS_COPY}.cta.${meta.ctaKey}`)
    : t(`${TOOLS_COPY}.openTool`);

  return (
    <article className={`cm-partner-tools__card${live ? " cm-partner-tools__card--live" : ""}`} id={`tool-${toolKey}`}>
      <div className="cm-partner-tools__badges">
        <span className="cm-partner-tools__badge">
          {t("realEstate.casaMapa.advertise.freeTools.freeLabel")}
        </span>
        {live ? (
          <span className="cm-partner-tools__badge cm-partner-tools__badge--live">
            {t(`${TOOLS_COPY}.liveLabel`)}
          </span>
        ) : (
          <span className="cm-partner-tools__badge cm-partner-tools__badge--soon">
            {t(`${TOOLS_COPY}.comingSoon`)}
          </span>
        )}
      </div>
      <h3>{t(`${AD_ITEMS}.${toolKey}.name`)}</h3>
      <p className="cm-partner-tools__body">{t(`${AD_ITEMS}.${toolKey}.body`)}</p>
      <p className="cm-partner-tools__outcome">{t(`${AD_ITEMS}.${toolKey}.outcome`)}</p>
      <p className="cm-partner-tools__howto muted">{t(`${AD_ITEMS}.${toolKey}.howToUse`)}</p>
      <div className="cm-partner-tools__footer">
        {available ? (
          <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={destination}>
            {ctaLabel}
          </Link>
        ) : (
          <Link className="partner-dashboard-btn" to={destination}>
            {t(`${TOOLS_COPY}.previewArea`)}
          </Link>
        )}
      </div>
    </article>
  );
}

function ToolGrid({ toolKeys, path, t, labelledBy }) {
  return (
    <div className="cm-partner-tools__grid" aria-labelledby={labelledBy}>
      {toolKeys.map((toolKey) => (
        <FreeToolCard key={toolKey} toolKey={toolKey} path={path} t={t} />
      ))}
    </div>
  );
}

export default function PartnerCasaMapaMarketPage() {
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [trends, setTrends] = useState(null);
  const [trendsError, setTrendsError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchCasaMapaPartnerMarketTrends({ days: 30 })
      .then((result) => {
        if (!cancelled) setTrends(result.trends || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setTrendsError(err?.message || t("realEstate.casaMapa.admin.partnerMarketTrends.loadError"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <div className="cm-partner-market">
      <header className="cm-partner-tools__hero">
        <p className="cm-partner-tools__eyebrow">{t(`${MARKET_COPY}.eyebrow`)}</p>
        <h1>{t(`${MARKET_COPY}.title`)}</h1>
        <p className="muted">{t(`${MARKET_COPY}.intro`)}</p>
      </header>

      <PartnerCasaMapaMarketSnapshot
        trends={trends}
        error={trendsError}
        path={path}
        t={t}
        formatMoney={formatMoney}
      />

      <section aria-labelledby="cm-partner-market-intel-title">
        <div className="cm-partner-market__section-head">
          <h2 id="cm-partner-market-intel-title">{t(`${MARKET_COPY}.intelTitle`)}</h2>
          <p className="muted">{t(`${MARKET_COPY}.intelIntro`)}</p>
        </div>
        <ToolGrid
          toolKeys={CASAMAPA_PARTNER_MARKET_INTEL_TOOL_KEYS}
          path={path}
          t={t}
          labelledBy="cm-partner-market-intel-title"
        />
      </section>

      <section aria-labelledby="cm-partner-market-workspace-title">
        <div className="cm-partner-market__section-head">
          <h2 id="cm-partner-market-workspace-title">{t(`${MARKET_COPY}.workspaceTitle`)}</h2>
          <p className="muted">{t(`${MARKET_COPY}.workspaceIntro`)}</p>
        </div>
        <ToolGrid
          toolKeys={CASAMAPA_PARTNER_WORKSPACE_TOOL_KEYS}
          path={path}
          t={t}
          labelledBy="cm-partner-market-workspace-title"
        />
      </section>

      <section className="partner-panel cm-partner-market__related">
        <h2>{t(`${MARKET_COPY}.relatedTitle`)}</h2>
        <p className="muted">{t(`${MARKET_COPY}.relatedBody`)}</p>
        <div className="cm-partner-market__related-links">
          <Link className="partner-dashboard-btn" to={path("listings")}>
            {t("realEstate.casaMapa.admin.nav.listings")}
          </Link>
          <Link className="partner-dashboard-btn" to={path("leads")}>
            {t("realEstate.casaMapa.admin.nav.leads")}
          </Link>
          <Link className="partner-dashboard-btn" to={path("directory")}>
            {t("realEstate.casaMapa.admin.nav.directory")}
          </Link>
        </div>
      </section>
    </div>
  );
}
